import { useEffect, useRef, useState } from "react";

import {
  Button,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import {
  ActivationFlow,
  ClientAppEvent,
  CommandLinkName,
  SUPPORT_EMAIL,
} from "../../../constants";
import { useAuthContext } from "../../../hooks/useAuthContext/useAuthContext";
import { ErrorTypography } from "../../ErrorTypography/ErrorTypography";
import { useCommandLinkPreview } from "../hooks/useCommandLinkPreview";

const LOGIN_ERROR_MESSAGE = `Login information not found. Please contact ${SUPPORT_EMAIL}.`;

const ACTIVATE_TOOLTIP =
  "Signs you in and takes you to the page where you choose a password for your Evotrux account.";

interface VoluntellActivationPreviewData {
  email: string;
  deterministic_password: string;
  flow: string;
  shipment_id: number | null;
}

interface VoluntellActivationLandingProps {
  token: string;
}

/**
 * Nothing is fetched or authenticated until the recipient presses the button.
 * The token carries a deterministic password and the page used to redeem it on
 * mount, so every email security scanner that rendered the link completed a
 * login as the user, polluting activation metrics and handing out credentials.
 */
export function VoluntellActivationLanding(
  props: VoluntellActivationLandingProps,
) {
  const { token } = props;
  const posthog = usePostHog();
  const { login, isLoading, attemptedVoluntellLogin, loginErrorMsg } =
    useAuthContext();
  const [activationRequested, setActivationRequested] = useState(false);
  const hasAttemptedLogin = useRef(false);
  const hasCapturedPageShownRef = useRef(false);

  const {
    data,
    loading: previewLoading,
    error: previewError,
  } = useCommandLinkPreview<VoluntellActivationPreviewData>({
    commandName: CommandLinkName.ACTIVATE_VOLUNTELL_USER,
    token,
    enabled: activationRequested,
  });

  useEffect(() => {
    if (hasCapturedPageShownRef.current) return;
    hasCapturedPageShownRef.current = true;
    posthog?.capture(ClientAppEvent.VOLUNTELL_ACTIVATION_LINK_PAGE_SHOWN, {
      commandName: CommandLinkName.ACTIVATE_VOLUNTELL_USER,
    });
  }, [posthog]);

  useEffect(() => {
    if (data && !hasAttemptedLogin.current) {
      hasAttemptedLogin.current = true;
      login({
        email: data.email,
        password: data.deterministic_password,
        voluntellLogin: true,
        shipmentId: data.shipment_id != null ? String(data.shipment_id) : null,
        flow: ActivationFlow.VOLUNTELL_COMMAND_LINK,
      });
    }
  }, [data, login]);

  if (!activationRequested) {
    return (
      <Paper elevation={2} sx={{ p: { xs: 3, sm: 6 } }}>
        <Stack spacing={3}>
          <Typography variant="h3" textAlign="center" fontWeight={600}>
            Activate your Evotrux account
          </Typography>
          <Typography variant="body1" textAlign="center">
            Your account is ready. Activate it to choose a password and start
            using Evotrux.
          </Typography>
          <Tooltip title={ACTIVATE_TOOLTIP}>
            <span>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setActivationRequested(true)}
                sx={{ textTransform: "none", py: 1.25 }}
              >
                Activate my account
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>
    );
  }

  if (previewError) {
    return <ErrorTypography error={previewError} />;
  }

  if (attemptedVoluntellLogin && !isLoading && loginErrorMsg != null) {
    return (
      <Typography color="error" textAlign="center" variant="h6">
        {LOGIN_ERROR_MESSAGE}
      </Typography>
    );
  }

  return (
    <Stack spacing={2} alignItems="center">
      <CircularProgress />
      <Typography>
        {previewLoading
          ? "Checking your activation link..."
          : "Activating your account..."}
      </Typography>
    </Stack>
  );
}
